import { enableStaticRendering } from 'mobx-react';
import { action, computed, makeObservable, observable } from 'mobx';
import { ThirdPartyTracker } from '@/utils/Tracker';
import StoreBase from './Base';
import { ABTestExperimentData, UniversalCollect } from './types/abtest';
import { MobxStore } from './types';
import { intervalPromise, isBroswer } from '@/utils';
import Cookies from 'js-cookie';
import { ACCESSORIES_PIDS_SET } from '@/const/accessories_pid';
import TrackerQueue from '@/utils/Tracker/TrackerQueue';

enableStaticRendering(typeof window === 'undefined');

// 【技术迭代】Docs： https://arashivision.feishu.cn/wiki/L6Twwm8znibDVqkrXRBcrEvPnjg
class ABTest extends StoreBase<'abtest'> {
  constructor(props: MobxStore) {
    super(props);
    makeObservable(this);

    // 监听cookie 设置完成后的回调
    if (isBroswer) {
      this.addVwoCookieEventListener();
    }
  }

  @observable
  abTestData: Array<ABTestExperimentData> = [];

  @observable
  universalCollect: UniversalCollect = {};

  /** VWO prime实验不展示Prime按钮的组别 */
  @observable
  isInVWOabtNotShowPrime = true;

  @action
  updateIsInVWOabtNotShowPrime = (data: boolean) => {
    this.isInVWOabtNotShowPrime = data;
  };

  // 监听cookie 设置完成后VWO的回调处理
  @action
  addVwoCookieEventListener = () => {
    window.addEventListener('CookieConsentComplete', (event: CustomEvent) => {
      const { statistics } = event.detail;
      // 如果不同意Cookie政策，VWO不生效，直接加载Prime
      if (!statistics) return this.updateIsInVWOabtNotShowPrime(false);
      // 如果同意cookie政策，判断cookie在哪一组
      intervalPromise(() => {
        return !!window.VWO;
      }, 200)
        .then(() => {
          if (window.VWO) {
            // 如果是vwo 原组（==1）不展示，， 变体1（==2）则展示，vwo没有加载，也要展示
            const campaignID = window.isProd ? 12 : 10;
            this.updateIsInVWOabtNotShowPrime(this.isVwoTargetingResult(campaignID, '1'));
          }
        })
        .catch((e) => {
          console.error(e);
          // 超时直接加载Prime
          this.updateIsInVWOabtNotShowPrime(false);
        });
    });
  };

  @computed
  get abTestDataCache() {
    const cache: Map<string, ABTestExperimentData> = new Map();
    this.abTestData.forEach((exp) => {
      if (!cache.has(exp.flagName)) cache.set(exp.flagName, exp);
    });

    return cache;
  }

  // 业务方有除了：add_to_cart，begin_checkout，checkout, purchase之外的固定埋点，
  //  需要进行自定义上报Action Tracking事件的，请将函数添加此object中进行维护👇（方便后续统一删）
  //  具体组件通过这个mobx object map获取KPI event。活动下架时删除这个object map中的key-value就可以保证具体组件不会上报
  customKPIList: Record<string, () => void> = {};

  sendCustomKPI = (KPI: string) => {
    const event = this.customKPIList[KPI];
    event && event();
  };

  // 用于判断ABTest业务逻辑的主要方法
  isTargetingResult = (
    flagName: ABTestExperimentData['flagName'],
    targetingResult: ABTestExperimentData['targetingResult']
  ) => {
    const experiment = this.abTestDataCache.get(flagName);
    if (!experiment || experiment?.noExperiment) return false;
    return experiment.targetingResult === targetingResult;
  };

  // 判断实验是否启用，无论组别
  isExpOn = (flagName: ABTestExperimentData['flagName']) => {
    const experiment = this.abTestDataCache.get(flagName);
    if (!experiment || experiment?.noExperiment) return false;
    return true;
  };

  // 页面级别固定KPI:
  fixedSendHitMethods = {
    add_to_cart: (commodity: { id?: number; product?: number }) => {
      const abTestKPIList = this.universalCollect['add_to_cart'];
      if (abTestKPIList) {
        const hasReportedSet = new Set();
        abTestKPIList.forEach((item) => {
          if (!hasReportedSet.has(item?.KPI) || item?.p) {
            let needSendHit = true;
            if (item?.filter?.commodityId && commodity.id) {
              const hasCommodity = item.filter.commodityId?.includes(commodity?.id);
              if (!hasCommodity) needSendHit = false;
            }
            if (item?.filter?.productId && commodity.product) {
              const hasProduct = item.filter.productId?.includes(commodity?.product);
              if (!hasProduct) needSendHit = false;
            }

            if (needSendHit) {
              if (item?.p) window?.collectEvent?.('beconEvent', item?.KPI || 'add_to_cart');
              else ThirdPartyTracker.ABTastyTracker.sendEvent({ KPI: item?.KPI });
            }
          }
        });
      }
    },
    begin_checkout: (
      selectedCartItems: Array<{
        commodityId: number;
        productId: number;
      }>
    ) => {
      const abTestKPIList = this.universalCollect['begin_checkout'];
      if (abTestKPIList) {
        const hasReportedSet = new Set();
        abTestKPIList.forEach((item) => {
          if (!hasReportedSet.has(item?.KPI)) {
            let needSendHit = true;
            if (item?.filter?.commodityId) {
              const hasCommodity = selectedCartItems.find((i) => item.filter.commodityId?.includes(i?.commodityId));
              if (!hasCommodity) needSendHit = false;
            }
            if (item?.filter?.productId) {
              const hasProduct = selectedCartItems.find((i) => item.filter.productId?.includes(i?.productId));
              if (!hasProduct) needSendHit = false;
            }
            if (item?.filter?.onlyAccessory) {
              const isOnlyIncludesAccessories = selectedCartItems.every((i) => ACCESSORIES_PIDS_SET.has(i?.productId));
              if (!isOnlyIncludesAccessories) needSendHit = false;
            }

            if (needSendHit) {
              ThirdPartyTracker.ABTastyTracker.sendEvent({ KPI: item?.KPI });
              hasReportedSet.add(item?.KPI);
            }
          }
        });
      }
    },
    checkout: (orderCartItems: Array<{ commodityId: number; productId: number }>) => {
      // 默认上报事件：
      ThirdPartyTracker.ABTastyTracker.sendEvent({ KPI: 'checkout' });
      const abTestKPIList = this.universalCollect['checkout'];
      if (abTestKPIList) {
        const hasReportedSet = new Set();
        abTestKPIList.forEach((item) => {
          if (!hasReportedSet.has(item?.KPI)) {
            let needSendHit = true;
            if (item?.filter?.commodityId) {
              const hasCommodity = orderCartItems.find((i) => item.filter.commodityId?.includes(i?.commodityId));
              if (!hasCommodity) needSendHit = false;
            }
            if (item?.filter?.productId) {
              const hasProduct = orderCartItems.find((i) => item.filter.productId?.includes(i?.productId));
              if (!hasProduct) needSendHit = false;
            }
            if (item?.filter?.onlyAccessory) {
              const isOnlyIncludesAccessories = orderCartItems.every((i) => ACCESSORIES_PIDS_SET.has(i?.productId));
              if (!isOnlyIncludesAccessories) needSendHit = false;
            }

            if (needSendHit) {
              ThirdPartyTracker.ABTastyTracker.sendEvent({ KPI: item?.KPI });
              hasReportedSet.add(item?.KPI);
            }
          }
        });
      }
    },
    purchase: (
      orderItemInfos: Array<{ commodityId: number; productId: number }>,
      orderPaymentInfo: { currency: string; totalAmount: number },
      orderNumber: string
    ) => {
      const abTestKPIList = this.universalCollect['purchase'];
      if (abTestKPIList) {
        const hasReportedSet = new Set();
        abTestKPIList.forEach((item) => {
          if (!hasReportedSet.has(item?.KPI) || item?.p) {
            let needSendHit = true;
            if (item?.filter?.commodityId) {
              const hasCommodity = orderItemInfos.find((i) => item.filter.commodityId?.includes(i?.commodityId));
              if (!hasCommodity) needSendHit = false;
            }
            if (item?.filter?.productId) {
              const hasProduct = orderItemInfos.find((i) => item.filter.productId?.includes(i?.productId));
              if (!hasProduct) needSendHit = false;
            }
            if (item?.filter?.onlyAccessory) {
              const isOnlyIncludesAccessories = orderItemInfos.every((i) => ACCESSORIES_PIDS_SET.has(i?.productId));
              if (!isOnlyIncludesAccessories) needSendHit = false;
            }

            if (needSendHit) {
              if (item?.p) {
                if (!window.collectEvent) TrackerQueue?.customQueue?.push(item?.KPI || 'purchase');
                else window?.collectEvent?.(item?.KPI || 'purchase');
              } else
                ThirdPartyTracker.ABTastyTracker.sendTranscation({
                  KPI: item?.KPI || 'purchase',
                  orderNumber,
                  currency: orderPaymentInfo.currency,
                  revenue: orderPaymentInfo.totalAmount,
                });
              hasReportedSet.add(item?.KPI);
            }
          }
        });
      }
    },
  };

  // VWO ABT 活动组别是否启用
  isVwoTargetingResult = (
    campaignId: number,
    // 组别按照数字排序，原组=1，变体1=2，变体2=3
    targetingResult: number | string
  ) => {
    const abTestNumber = Cookies.get(`_vis_opt_exp_${campaignId}_combi`);
    const debugTestNumber = Cookies.get(`debug_vis_opt_exp_${campaignId}_combi`);

    // debug优先
    if (debugTestNumber === `${targetingResult}`) return true;

    if (abTestNumber === `${targetingResult}`) {
      return true;
    }
    return false;
  };

  // X4潜水商详ABT，实验获取失败 or 关闭默认为实验组
  @computed
  get isX4DivingOriginal() {
    const flagName = 'details_x4_dive';
    return this.isTargetingResult(flagName, 'dive-regular');
  }
}

export default ABTest;
